import { Node, mergeAttributes } from '@tiptap/core'
import Link from '@tiptap/extension-link'
import { Plugin } from '@tiptap/pm/state'
import { NodeViewWrapper, ReactNodeViewRenderer, ReactNodeViewProps } from '@tiptap/react'
import { store } from './store'
import { BlobImage } from '../features/documents/BlobImage'
import { blobIdFromStoredRef, ensureBlobUrls } from '../features/documents/attachementsSlice'

/**
 * tiptap extensions that keep blob-backed content out of persisted editor HTML: a node /
 * mark store only a stable `data-blob-id`, and the actual signed (SAS) URL is minted at
 * render / click time. parseHTML also recognises legacy absolute `.../blobs/{r}/{b}` URLs
 * baked into already-saved content, so old documents keep working after the cutover.
 */

const blobIdFromAnchor = (el: HTMLElement | null): string | null => {
    if (!el) return null
    const explicit = el.getAttribute('data-blob-id')
    if (explicit) return explicit
    const href = el.getAttribute('href')
    if (href && href.includes('/blobs/')) return blobIdFromStoredRef(href) ?? null
    return null
}

// --- Image node backed by a blobId, resolved to a signed URL by <BlobImage> ---

const BlobImageNodeView = (props: ReactNodeViewProps) => {
    const blobId = props.node.attrs.blobId as string | null
    return (
        <NodeViewWrapper as="span" className="blob-image-node">
            {blobId ? <BlobImage blobId={blobId} /> : null}
        </NodeViewWrapper>
    )
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        blobImage: {
            setBlobImage: (attrs: { blobId: string }) => ReturnType
        }
    }
}

export const BlobImageNode = Node.create({
    name: 'blobImage',
    group: 'block',
    draggable: true,
    // Outrank the generic image extension so blob-backed <img> are claimed here, while
    // self-contained data-URL images (paste) still fall through to ResizableImage.
    priority: 1000,

    addAttributes() {
        return {
            blobId: {
                default: null,
                parseHTML: (el: HTMLElement) =>
                    el.getAttribute('data-blob-id') ?? blobIdFromStoredRef(el.getAttribute('src')) ?? null,
                renderHTML: (attrs: { blobId?: string | null }) =>
                    attrs.blobId ? { 'data-blob-id': attrs.blobId } : {},
            },
        }
    },

    parseHTML() {
        return [
            { tag: 'img[data-blob-id]' },
            { tag: 'img[src*="/blobs/"]' },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        // Serialise to <img data-blob-id="..."> — never a (short-lived) signed URL.
        return ['img', mergeAttributes(HTMLAttributes)]
    },

    addNodeView() {
        return ReactNodeViewRenderer(BlobImageNodeView)
    },

    addCommands() {
        return {
            setBlobImage:
                (attrs) =>
                ({ commands }) =>
                    commands.insertContent({ type: this.name, attrs }),
        }
    },
})

// --- Link mark that carries a blobId and mints a signed URL on click ---

export const BlobLinkExtension = Link.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            'data-blob-id': {
                default: null,
                parseHTML: (el: HTMLElement) => blobIdFromAnchor(el),
                renderHTML: (attrs: Record<string, unknown>) =>
                    attrs['data-blob-id'] ? { 'data-blob-id': attrs['data-blob-id'] } : {},
            },
        }
    },

    addProseMirrorPlugins() {
        const parentPlugins = this.parent?.() ?? []
        const clickPlugin = new Plugin({
            props: {
                handleDOMEvents: {
                    click: (_view, event) => {
                        const anchor = (event.target as HTMLElement)?.closest('a')
                        const blobId = blobIdFromAnchor(anchor)
                        if (!blobId) return false
                        event.preventDefault()
                        // store.dispatch accepts thunks; the typed signature here is the plain one.
                        ;(store.dispatch(ensureBlobUrls([blobId])) as unknown as { unwrap: () => Promise<Record<string, string>> })
                            .unwrap()
                            .then(minted => {
                                const url = minted[blobId] ?? store.getState().attachements.urls[blobId]?.url
                                if (url) window.open(url, '_blank', 'noopener,noreferrer')
                            })
                            .catch(() => { /* nothing actionable here */ })
                        return true
                    },
                },
            },
        })
        return [...parentPlugins, clickPlugin]
    },
})
